/**
 * environment.ts — KitchenEnv: pickup/skip decisions over a free grid (v2).
 *
 * Ported from `_proto_kitchen_v1.py` `KitchenEnv` (lines 290-490), rewritten
 * for the v2 PICKUP/SKIP reframe. The decision is isolated from movement:
 * the agent walks freely (BFS to the nearest undecided item — a fixed
 * policy, nothing learned) and at each item makes ONE binary decision.
 *
 * Each STEP:
 *   1. The env presents the NEXT undecided item (nearest uncollected item
 *      by BFS from the agent's current position). If none, episode ends.
 *   2. The agent decides PICKUP (1) or SKIP (0).
 *   3. PICKUP adds the item to the basket and yields reward = the change
 *      in scoreBasket. SKIP leaves the basket unchanged and yields 0.
 *   4. The item is marked DECIDED either way (no second chance).
 *
 * Movement is implicit and deterministic — the agent teleports along the
 * BFS path to the next item. Items are never obstacles.
 */
import {
  GRID, START_R, START_C, EMPTY, K, N_ACTIONS, SKIP, PICKUP,
  OFF_ITEM, OFF_BASKET, OFF_CTX, OFF_ACTION,
  N_FEATURES, BASKET_CAP, MAX_STEPS, R_SKIP,
} from './constants';
import { scoreBasket, pickupReward } from './items';
import type { Grid } from './shapes';

export interface StepOutcome {
  reward: number;
  done: boolean;
}

export interface DecisionContext {
  item: number;     // current item id in [0, K), or -1 if exhausted
  ctx: number;      // current context
  basket: number;   // current basket bitmask
}

export class KitchenEnv {
  truthInitial: Grid;
  truth: Grid;
  decided!: Uint8Array; // GRID*GRID, allocated in reset()
  pickedUp!: Uint8Array; // GRID*GRID, 1 if this cell's item was PICKED UP
  // 1 if this cell's item was a DUPLICATE encounter (its type was already in
  // the basket when the agent arrived). The renderer marks these distinctly
  // so the viewer can see the constraint in action: a duplicate is forced to
  // SKIP and the cell renders with a dim hatch, not the green pickup border
  // and not the plain dark-skip tint.
  duplicateDecided!: Uint8Array;
  r = START_R;
  c = START_C;
  prevR = START_R;   // for the renderer: position BEFORE this step's walk
  prevC = START_C;
  basket = 0; // bitmask of K items
  ctx: number;
  ctxInitial: number;
  steps = 0;
  done = false;
  prevScore = 0;
  basketCap: number;
  currentItem: number = -1; // item id under decision, or -1 if exhausted
  lastAction: number = -1;  // last PICKUP/SKIP (for renderer feedback)
  private flipAtStep: number | null;
  private flipTo: number | null;

  constructor(truth: Grid, ctx: number, flipAtStep: number | null = null,
              flipTo: number | null = null, basketCap: number = BASKET_CAP) {
    this.truthInitial = truth.slice();
    this.truth = truth.slice();
    this.ctxInitial = ctx;
    this.ctx = ctx;
    this.basketCap = basketCap;
    this.flipAtStep = flipAtStep;
    this.flipTo = flipTo;
    this.reset();
  }

  reset(): void {
    this.truth = this.truthInitial.slice();
    this.decided = new Uint8Array(GRID * GRID);
    this.pickedUp = new Uint8Array(GRID * GRID);
    this.duplicateDecided = new Uint8Array(GRID * GRID);
    this.r = START_R;
    this.c = START_C;
    this.prevR = START_R;
    this.prevC = START_C;
    this.basket = 0;
    this.ctx = this.ctxInitial;
    this.steps = 0;
    this.done = false;
    this.prevScore = 0;
    this.currentItem = -1;
    this.lastAction = -1;
    // Advance to the first item so the agent has something to decide on.
    this.advanceToNextItem();
  }

  /** BFS from the agent's current position to the nearest undecided item.
   *  Sets currentItem and updates position to that cell. Returns true if an
   *  item was found, false if the grid is exhausted (→ episode done). */
  advanceToNextItem(): boolean {
    // Collect undecided item cells.
    // Quick exit: if any undecided cell exists we BFS; else done.
    let any = false;
    for (let i = 0; i < GRID * GRID; i++) {
      if (this.truth[i] !== EMPTY && !this.decided[i]) { any = true; break; }
    }
    if (!any) { this.currentItem = -1; return false; }
    // BFS for nearest target. GRID=15 → 225 cells, trivial.
    const targets = new Uint8Array(GRID * GRID);
    for (let i = 0; i < GRID * GRID; i++) {
      if (this.truth[i] !== EMPTY && !this.decided[i]) targets[i] = 1;
    }
    const visited = new Uint8Array(GRID * GRID);
    const queueR = new Int32Array(GRID * GRID);
    const queueC = new Int32Array(GRID * GRID);
    let qHead = 0;
    let qTail = 0;
    queueR[qTail] = this.r;
    queueC[qTail] = this.c;
    qTail++;
    visited[this.r * GRID + this.c] = 1;
    let foundIdx = -1;
    while (qHead < qTail) {
      const cr = queueR[qHead];
      const cc = queueC[qHead];
      qHead++;
      if (targets[cr * GRID + cc]) { foundIdx = cr * GRID + cc; break; }
      // 8-connected neighbours.
      for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          if (dr === 0 && dc === 0) continue;
          const nr = cr + dr;
          const nc = cc + dc;
          if (nr < 0 || nr >= GRID || nc < 0 || nc >= GRID) continue;
          const ni = nr * GRID + nc;
          if (visited[ni]) continue;
          visited[ni] = 1;
          queueR[qTail] = nr;
          queueC[qTail] = nc;
          qTail++;
        }
      }
    }
    if (foundIdx < 0) { this.currentItem = -1; return false; }
    const fr = Math.floor(foundIdx / GRID);
    const fc = foundIdx % GRID;
    this.prevR = this.r;
    this.prevC = this.c;
    this.r = fr;
    this.c = fc;
    this.currentItem = this.truth[foundIdx];
    return true;
  }

  /** (N_ACTIONS, N_FEATURES) row-major stack. Two rows: SKIP and PICKUP.
   *  Identical except the action one-hot — the tree learns ONE rule
   *  "pickup × coffee × morning → +3" shared across the action dimension. */
  candidateFeatures(out: Float64Array): void {
    out.fill(0);
    if (this.currentItem < 0) return;
    // Item one-hot (same for both rows).
    for (let a = 0; a < N_ACTIONS; a++) out[a * N_FEATURES + OFF_ITEM + this.currentItem] = 1;
    // Basket one-hot (same for both rows).
    for (let i = 0; i < K; i++) {
      if (this.basket & (1 << i)) {
        for (let a = 0; a < N_ACTIONS; a++) out[a * N_FEATURES + OFF_BASKET + i] = 1;
      }
    }
    // Context one-hot (same for both rows).
    for (let a = 0; a < N_ACTIONS; a++) out[a * N_FEATURES + OFF_CTX + this.ctx] = 1;
    // Action one-hot — the ONLY thing that differs.
    out[SKIP * N_FEATURES + OFF_ACTION + SKIP] = 1;
    out[PICKUP * N_FEATURES + OFF_ACTION + PICKUP] = 1;
  }

  /** Snapshot of the decision-time context (for the bandit + teacher). */
  adjacentContext(): DecisionContext {
    return { item: this.currentItem, ctx: this.ctx, basket: this.basket };
  }

  /** Boolean mask over the N_ACTIONS actions for the current decision. PICKUP
   *  is illegal when the item's TYPE is already in the basket (the basket is
   *  a set — collecting a duplicate is a hard constraint violation, not a
   *  soft preference). SKIP is always legal. The bandit consults this mask
   *  at select() time and is structurally prevented from picking up a
   *  duplicate. The duplicate rule is still LEARNED by the head because
   *  counterfactual both-arm observe keeps feeding R_DUPLICATE as the PICKUP
   *  label; it's just not RELIED ON at decision time. This is the fix for the
   *  bug where TS sampling occasionally flipped a duplicate pickup despite
   *  the tree's mean Q having learned the rule. */
  legalActions(): Uint8Array {
    const mask = new Uint8Array(N_ACTIONS).fill(1);
    if (this.currentItem >= 0 && (this.basket & (1 << this.currentItem))) {
      mask[PICKUP] = 0;
    }
    return mask;
  }

  /** H3 hook: flip context mid-episode to test drift detection. On flip,
   *  the value of items already in the basket changes; prevScore is
   *  silently updated so the NEXT decision's delta reflects the new ctx. */
  private maybeFlip(): void {
    if (this.flipAtStep !== null && this.flipTo !== null
        && this.steps === this.flipAtStep) {
      this.ctx = this.flipTo;
      this.prevScore = scoreBasket(this.basket, this.flipTo);
    }
  }

  /** Apply the pickup/skip decision. Returns (reward, done). */
  step(action: number): StepOutcome {
    if (this.done) return { reward: 0, done: true };
    this.steps += 1;
    this.lastAction = action;
    let reward = R_SKIP;
    if (this.currentItem >= 0) {
      // Mark the current item DECIDED either way (no second chance).
      this.decided[this.r * GRID + this.c] = 1;
      // Flag duplicate encounters for the renderer. With the legal-mask
      // constraint these are ALWAYS skipped; the visual distinguishes them
      // from first-time skips so the viewer sees "this was forced — the
      // type was already collected" rather than reading it as a preference.
      if (this.basket & (1 << this.currentItem)) {
        this.duplicateDecided[this.r * GRID + this.c] = 1;
      }
      if (action === PICKUP) {
        // pickupReward applies R_DUPLICATE if the type is already in the
        // basket; otherwise the basket-score delta. Update prevScore only
        // on a non-duplicate pickup (a duplicate leaves the basket score
        // unchanged, so the next decision's delta baseline is unaffected).
        reward = pickupReward(this.basket, this.currentItem, this.ctx);
        this.basket |= (1 << this.currentItem);
        // Record the cell so the renderer can border it green (the basket
        // bitmask only tracks item TYPES; this tracks which cells were
        // actually picked up vs merely decided/skipped).
        this.pickedUp[this.r * GRID + this.c] = 1;
        const newScore = scoreBasket(this.basket, this.ctx);
        this.prevScore = newScore;
      }
    }
    // Apply the flip AFTER scoring this decision.
    this.maybeFlip();
    // End conditions.
    let nInBasket = 0;
    for (let i = 0; i < K; i++) if (this.basket & (1 << i)) nInBasket++;
    if (nInBasket >= this.basketCap || this.steps >= MAX_STEPS) {
      this.done = true;
      return { reward, done: true };
    }
    // Advance to the next undecided item; if none, episode ends.
    if (!this.advanceToNextItem()) this.done = true;
    return { reward, done: this.done };
  }
}
